import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Circle, CircleSlash, Lock } from 'lucide-react';
import { Badge, Button, buttonStyles, Section, Spinner, Switch, type Tone } from '@zt/ui';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { useApiMutation } from '../lib/mutation';
import { usePermissions, useSession } from '../lib/session';
import type {
  CriticalStock,
  Integration,
  NotificationSettings,
  PrivacySummary,
  RecentBalancePayments,
  SetupProgress,
  SystemStatus,
} from '../lib/types';
import { DashboardSales } from '../components/reports/DashboardSales';
import { DashboardWidgets } from '../components/reports/DashboardWidgets';

const STEP_LINKS: Record<string, string> = {
  business: '/isletme',
  brand: '/markalar',
  logo: '/markalar',
  colors: '/markalar',
  domain: '/magazalar',
  currency: '/magazalar',
  email_service: '/entegrasyonlar',
  search_console: '/google',
  ga4: '/olcumleme',
  gtm: '/olcumleme',
  merchant_center: '/google-alisveris',
  seo: '/arama-performansi',
  tax: '/fiyatlar',
  import_products: '/ice-aktarma',
  warehouse: '/stok',
  preview_store: '/magazalar',
  shipping: '/kargo',
  pos_application: '/entegrasyonlar',
  pos_connect: '/odeme-ayarlari',
  legal_pages: '/yasal-metinler',
  test_order: '/odeme-ayarlari',
  payment_tests: '/odemeler',
  go_live: '/magazalar',
};

function useSetup(enabled = true) {
  return useQuery({ queryKey: ['setup'], queryFn: () => api.get<SetupProgress>('/v1/admin/setup'), enabled });
}

function ProgressBar({ value, total, label }: { value: number; total: number; label: string }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div
      className="h-2 overflow-hidden rounded-full bg-line"
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={value}
    >
      <div className="h-full rounded-full bg-pine transition-[width]" style={{ width: `${pct}%` }} />
    </div>
  );
}

export function DashboardPage() {
  const t = useT();
  const session = useSession();
  const { can } = usePermissions();
  const setup = useSetup(can('org.settings.read'));
  const status = useQuery({
    queryKey: ['system-status'],
    queryFn: () => api.get<SystemStatus>('/v1/admin/system/status'),
    enabled: can('org.system.read'),
  });
  const integrations = useQuery({
    queryKey: ['integrations'],
    queryFn: () => api.get<Integration[]>('/v1/admin/integrations'),
    enabled: can('org.integration.read'),
  });

  const openReturns = useQuery({
    queryKey: ['returns', 'requested'],
    queryFn: () => api.get<Array<{ id: string }>>('/v1/admin/returns?status=requested'),
    enabled: can('orders.order.read'),
  });
  const privacyRequests = useQuery({
    queryKey: ['privacy', 'summary'],
    queryFn: () => api.get<PrivacySummary>('/v1/admin/privacy/summary'),
    enabled: can('privacy.request.read'),
  });
  const criticalStock = useQuery({
    queryKey: ['stock-critical'],
    queryFn: () => api.get<CriticalStock>('/v1/admin/stock/critical'),
    enabled: can('inventory.stock.read'),
  });
  const reviewPayments = useQuery({
    queryKey: ['payments', 'requires_action'],
    queryFn: () => api.get<Array<{ id: string }>>('/v1/admin/payments?status=requires_action'),
    enabled: can('payments.payment.read'),
  });
  const dealerPayments = useQuery({
    queryKey: ['balance-payments', 'recent'],
    queryFn: () => api.get<RecentBalancePayments>('/v1/admin/ledger/balance-payments'),
    enabled: can('b2b.ledger.read'),
  });

  const alerts: string[] = [];
  if (openReturns.data?.length)
    alerts.push(t('admin.dashboard.alertReturns', { count: openReturns.data.length }));
  if (privacyRequests.data?.overdue)
    alerts.push(t('admin.dashboard.alertPrivacyOverdue', { count: privacyRequests.data.overdue }));
  else if (privacyRequests.data?.open)
    alerts.push(t('admin.dashboard.alertPrivacy', { count: privacyRequests.data.open }));
  if (criticalStock.data?.total)
    alerts.push(t('admin.dashboard.alertLowStock', { count: criticalStock.data.total }));
  if (reviewPayments.data?.length)
    alerts.push(t('admin.dashboard.alertPaymentReview', { count: reviewPayments.data.length }));
  if (dealerPayments.data?.requiresAction)
    alerts.push(t('admin.dashboard.alertBalanceReview', { count: dealerPayments.data.requiresAction }));
  integrations.data
    ?.filter((i) => i.status === 'ERROR')
    .forEach((i) =>
      alerts.push(t('admin.dashboard.alertIntegration', { name: t(`integrations.${i.key}.name`) })),
    );
  if (status.data) {
    if (status.data.mail === 'not_configured') alerts.push(t('admin.dashboard.alertMail'));
    if (status.data.redis !== 'ok') alerts.push(t('admin.dashboard.alertRedis'));
    status.data.domains.forEach((d) => {
      if (d.tlsStatus === 'error') alerts.push(t('admin.dashboard.alertTlsError', { host: d.hostname }));
      else if (d.daysLeft !== null && d.daysLeft < 21)
        alerts.push(t('admin.dashboard.alertTls', { host: d.hostname, days: d.daysLeft }));
    });
    const backup = status.data.lastBackup;
    const restore = status.data.lastRestoreTest;
    if (!backup) alerts.push(t('admin.dashboard.alertBackup'));
    else if (backup.status === 'failed') alerts.push(t('admin.dashboard.alertBackupFailed'));
    else if (Date.now() - new Date(backup.startedAt).getTime() > 26 * 3600_000)
      alerts.push(t('admin.dashboard.alertBackupStale'));
    if (restore?.status === 'failed') alerts.push(t('admin.dashboard.alertRestoreFailed'));
  }

  return (
    <PageFrame
      helpKey="page.dashboard"
      title={t('admin.dashboard.greeting', { name: session.data?.user.name.split(' ')[0] ?? '' })}
    >
      {setup.data && (
        <Section
          title={t('admin.dashboard.setupHeading')}
          description={t('admin.dashboard.setupProgress', {
            done: setup.data.completed,
            total: setup.data.available,
          })}
          action={
            <Link to="/kurulum" className={buttonStyles({ variant: 'primary', size: 'sm' })}>
              {t('admin.dashboard.continueSetup')}
            </Link>
          }
        >
          <ProgressBar
            value={setup.data.completed}
            total={setup.data.available}
            label={t('admin.dashboard.setupHeading')}
          />
        </Section>
      )}
      <Section title={t('admin.dashboard.attentionHeading')}>
        {status.isLoading || integrations.isLoading ? (
          <Spinner label={t('common.loading')} />
        ) : alerts.length === 0 ? (
          <p className="text-ink-2">{t('admin.dashboard.nothingToDo')}</p>
        ) : (
          <ul className="divide-y divide-line rounded-md border border-line bg-surface">
            {alerts.map((a) => (
              <li key={a} className="flex items-start gap-3 px-4 py-3">
                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-amber" aria-hidden />
                <span className="text-[0.9375rem]">{a}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title={t('admin.dashboard.salesHeading')}>
        <DashboardSales />
      </Section>
      {dealerPayments.data?.hasDealers && <DealerPayments data={dealerPayments.data} />}
      <DashboardWidgets />
    </PageFrame>
  );
}

/**
 * Son bayi ödemeleri (F7-43): bayilerin Hesabım → Cari hesabım'dan kartla yaptığı ödemeler (son 30 gün, en yeni 10)
 * ve bu ödemelerde uyarı alıcılarına giden e-postanın aç/kapat düğmesi.
 */
function DealerPayments({ data }: { data: RecentBalancePayments }) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const notifications = useQuery({
    queryKey: ['settings', 'notifications'],
    queryFn: () => api.get<NotificationSettings>('/v1/admin/settings/notifications'),
    enabled: can('org.settings.read'),
  });
  const toggle = useApiMutation(
    (on: boolean) =>
      api.put('/v1/admin/settings/notifications', { ...notifications.data, dealerPaymentAlerts: on }),
    { invalidate: [['settings', 'notifications']], success: t('common.saved') },
  );
  return (
    <Section
      title={t('admin.balancePayments.recent.title')}
      description={t('admin.balancePayments.recent.lead')}
      action={
        <span className="flex items-center gap-2">
          <Link to="/cari-hesaplar" className={buttonStyles({ variant: 'secondary', size: 'sm' })}>
            {t('admin.balancePayments.recent.all')}
          </Link>
          <HelpTip k="dashboard.dealer_payments" />
        </span>
      }
    >
      {data.items.length === 0 ? (
        <p className="text-ink-2">{t('admin.balancePayments.recent.empty', { days: data.sinceDays })}</p>
      ) : (
        <ul
          className="divide-y divide-line rounded-md border border-line bg-surface"
          aria-label={t('admin.balancePayments.recent.title')}
        >
          {data.items.map((p) => (
            <li
              key={p.id}
              className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-4 py-3"
            >
              <span className="min-w-0">
                <Link
                  to="/cari-hesaplar/$id"
                  params={{ id: p.companyId }}
                  className="break-words font-medium text-pine hover:underline"
                >
                  {p.companyName}
                </Link>
                <span className="block text-xs text-ink-3">
                  {formatDate(p.capturedAt, true)} · {t(`admin.balancePayments.providers.${p.provider}`)} ·{' '}
                  <span className="font-mono">{p.reference}</span>
                  {p.installmentCount !== null &&
                    p.installmentCount > 1 &&
                    ` · ${t('admin.balancePayments.installments', { count: p.installmentCount })}`}
                  {p.environment === 'sandbox' && ` · ${t('admin.balancePayments.sandbox')}`}
                </span>
              </span>
              <span className="tabular font-semibold">{money(p.amountMinor, p.currency)}</span>
            </li>
          ))}
        </ul>
      )}
      {notifications.data &&
        (can('org.settings.update') ? (
          <span className="mt-3 flex items-center gap-2">
            <Switch
              checked={notifications.data.dealerPaymentAlerts}
              onCheckedChange={(v) => toggle.mutate(v)}
              label={t('admin.balancePayments.recent.email')}
            />
            <span className="text-sm">{t('admin.balancePayments.recent.email')}</span>
            <HelpTip k="dashboard.dealer_payment_email" />
          </span>
        ) : (
          <p className="mt-3 text-sm text-ink-2">
            {notifications.data.dealerPaymentAlerts
              ? t('admin.balancePayments.recent.emailOn')
              : t('admin.balancePayments.recent.emailOff')}
          </p>
        ))}
    </Section>
  );
}

const statusTone: Record<string, Tone> = {
  done: 'moss',
  pending: 'amber',
  skipped: 'slate',
  locked: 'neutral',
};
const statusIcon = { done: CheckCircle2, pending: Circle, skipped: CircleSlash, locked: Lock };

export function SetupPage() {
  const t = useT();
  const { can } = usePermissions();
  const setup = useSetup();
  const mark = useApiMutation(
    (v: { key: string; status: 'skipped' | 'pending' }) =>
      api.patch<SetupProgress>(`/v1/admin/setup/steps/${v.key}`, { status: v.status }),
    {
      invalidate: [['setup']],
    },
  );
  return (
    <PageFrame helpKey="page.setup">
      {!setup.data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          <div className="space-y-2">
            <p className="text-ink-2">
              {t('admin.dashboard.setupProgress', {
                done: setup.data.completed,
                total: setup.data.available,
              })}
            </p>
            <ProgressBar
              value={setup.data.completed}
              total={setup.data.available}
              label={t('admin.dashboard.setupHeading')}
            />
          </div>
          {/* Adımlar gerçek bir sıra olduğu için numaralıdır (spesifikasyon §27). */}
          <ol className="divide-y divide-line rounded-md border border-line bg-surface">
            {setup.data.steps.map((s) => {
              const Icon = statusIcon[s.status];
              const link = STEP_LINKS[s.key];
              return (
                <li key={s.key} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                  <span className="tabular w-6 text-end font-cond text-ink-3">{s.number}</span>
                  <Icon
                    className={s.status === 'done' ? 'h-5 w-5 text-moss' : 'h-5 w-5 text-ink-3'}
                    aria-hidden
                  />
                  <span className={s.status === 'locked' ? 'flex-1 text-ink-3' : 'flex-1 font-medium'}>
                    {t(`setupSteps.${s.key}`)}
                  </span>
                  {s.status === 'locked' ? (
                    <span className="text-sm text-ink-3">{t('common.lockedPhase', { phase: s.phase })}</span>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Badge tone={statusTone[s.status]}>{t(`setupStatus.${s.status}`)}</Badge>
                      {link && s.status !== 'done' && (
                        <Link to={link} className={buttonStyles({ size: 'sm' })}>
                          {t('admin.setup.go')}
                        </Link>
                      )}
                      {s.status === 'pending' && can('org.setup.manage') && (
                        <span className="flex items-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => mark.mutate({ key: s.key, status: 'skipped' })}
                          >
                            {t('admin.setup.skip')}
                          </Button>
                          <HelpTip k="setup.skip_step" />
                        </span>
                      )}
                      {s.status === 'skipped' && can('org.setup.manage') && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => mark.mutate({ key: s.key, status: 'pending' })}
                        >
                          {t('admin.setup.unskip')}
                        </Button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </>
      )}
    </PageFrame>
  );
}
